import { fromZonedTime } from 'date-fns-tz';
import { motion } from 'motion/react';
import { useBookingContext } from '../../contexts/BookingContext';
import { useCalendar } from '../../hooks/useCalendar';
import { useWeekdays } from '../../hooks/useWeekdays';

interface CalendarProps {
	month: number;
	selectedDate: string;
	setSelectedDate: (date: string) => void;
	year: number;
}

const getCalendarItemClassNames = (isAvailable: boolean, isToday: boolean, isSelected: boolean) => {
	const classnames = ['calendar__item'];

	if (!isAvailable) classnames.push('calendar__item--unavailable');
	if (isToday) classnames.push('calendar__item--today');
	if (isSelected) classnames.push('calendar__item--selected');

	return classnames.join(' ');
};

export default function Calendar({ month, selectedDate, setSelectedDate, year }: CalendarProps) {
	const { availability, locale, timezone } = useBookingContext();
	const { entries, weekdayOffset } = useCalendar({ timeSlots: availability.options, locale, month, year });
	const { weekdaysShort } = useWeekdays(locale);

	const calendarDayStyle = { '--grid-offset': weekdayOffset } as React.CSSProperties;

	const transition = (dayNumber: number) => ({
		duration: 0.5,
		delay: 0.015 * dayNumber,
		animation: { ease: 'easeInOut' },
	});

	const variants = {
		hidden: { transform: 'translateY(-.5rem)', opacity: 0 },
		visible: { transform: 'translateY(0)', opacity: 1 },
	};

	return (
		<div className="calendar" aria-label={`Calendar for ${new Date(year, month).toLocaleDateString(locale, { month: 'long', year: 'numeric' })}`}>
			<div className="calendar__head">
				{weekdaysShort.map((label, index) => (
					<span key={index} className="calendar__item">
						{label}
					</span>
				))}
			</div>

			<div className="calendar__days" style={calendarDayStyle}>
				{entries.map(({ date, dayNumber, isAvailable, isToday }) => {
					const isSelected = selectedDate === date;
					const className = getCalendarItemClassNames(isAvailable, isToday, isSelected);
					const zonedDate = fromZonedTime(`${date}T12:00:00`, timezone.current);
					const dateString = new Intl.DateTimeFormat(locale, {
						year: 'numeric',
						weekday: 'long',
						month: 'long',
						day: 'numeric',
						timeZone: timezone.current,
					}).format(zonedDate);

					return isAvailable ? (
						<motion.button
							key={date}
							type="button"
							className={className}
							onClick={() => setSelectedDate(date)}
							data-color-scheme={isSelected ? 'highlight' : undefined}
							title={dateString}
							aria-label={`${dayNumber}, ${dateString}${isSelected ? ', selected' : ''}`}
							initial="hidden"
							whileInView="visible"
							exit="hidden"
							variants={variants}
							transition={transition(parseInt(dayNumber))}
						>
							{dayNumber}
						</motion.button>
					) : (
						<motion.span
							key={date}
							className={className}
							title={dateString}
							aria-label={`${dayNumber}, unavailable`}
							initial="hidden"
							whileInView="visible"
							exit="hidden"
							variants={variants}
							transition={transition(parseInt(dayNumber))}
						>
							{dayNumber}
						</motion.span>
					);
				})}
			</div>
		</div>
	);
}
